import React, { useState, useEffect } from 'react';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { InputV2 } from '../InputV2/InputV2';
import { useTranslation } from 'react-i18next';

interface NumberCounterProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  showRangeError?: boolean;
}

export const NumberCounter: React.FC<NumberCounterProps> = ({
  value,
  onChange,
  min = 0,
  max = 10,
  step = 1,
  placeholder = "0",
  className = "",
  disabled = false,
  error = false,
  showRangeError: showRangeErrorProp = false
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value.toString());
  const [showRangeError, setShowRangeError] = useState(showRangeErrorProp);

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue === '') {
      setInputValue(newValue);
      onChange(min);
      setShowRangeError(false);
    } else {
      const numValue = parseInt(newValue);
      if (!isNaN(numValue) && numValue >= min && numValue <= max) {
        const cleanValue = numValue.toString();
        setInputValue(cleanValue);
        onChange(numValue);
        setShowRangeError(false);
      } else {
        setShowRangeError(true);
      }
    }
  };

  const handleBlur = () => {
    if (inputValue === '' || isNaN(parseInt(inputValue))) {
      setInputValue(value.toString());
    }
  };

  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  return (
    <div className={className}>
      <div className="flex items-center space-x-2">
        <ButtonV2
          type="button"
          variant="outline"
          size="icon"
          onClick={handleDecrement}
          disabled={disabled || value <= min}
          className="h-10 w-10"
        >
          <span className="text-lg font-semibold">-</span>
        </ButtonV2>
        
        <InputV2
          type="number"
          min={min}
          max={max}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={`text-center ${error ? 'border-red-500' : ''}`}
        />
        
        <ButtonV2
          type="button"
          variant="outline"
          size="icon"
          onClick={handleIncrement}
          disabled={disabled || value >= max}
          className="h-10 w-10"
        >
          <span className="text-lg font-semibold">+</span>
        </ButtonV2>
      </div>
      {showRangeError && (
        <p className="text-red-500 text-sm mt-1">
          {t("errors.number_range", { min, max })}.
        </p>
      )}
    </div>
  );
}

export default NumberCounter;